import config from '../config';
import axios from 'axios';
import { getBearerToken } from '../util/cookie';

// Default weekly availability applied automatically right after signup:
// Monday-Friday, 09:00-17:00, in the browser's local timezone. Same shape
// CalendarAvailabilitySetting.save() posts, so it round-trips cleanly
// with the existing get/save thunks below.
export const DEFAULT_AVAILABILITY_DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday',
];
export const DEFAULT_AVAILABILITY_FROM = '09:00';
export const DEFAULT_AVAILABILITY_TO = '17:00';

const getTimezoneOffset = tz => {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
    const parts = fmt.formatToParts(new Date());
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    if (!offsetPart) return '+00:00';
    const raw = offsetPart.value; // e.g. "GMT+5:30" or "GMT-8" or "GMT"
    const match = raw.match(/GMT([+-]?\d{1,2}(?::?\d{2})?)/);
    if (!match) return '+00:00';
    const sign = match[1].startsWith('-') ? '-' : '+';
    const nums = match[1].replace(/^[+-]/, '');
    const [h, m] = nums.includes(':') ? nums.split(':') : [nums, '0'];
    return `${sign}${h.padStart(2, '0')}:${(m || '0').padStart(2, '0')}`;
  } catch (e) {
    return '+00:00';
  }
};

// Browser's local timezone in the { utcOffset, zoneName } shape the
// calendarAvailability doc stores under time.timeZone. Used to backfill a
// missing timezone on dashboard mount for users who have none set yet —
// see CoachDashboardPage / SaaSClientDashboard. Only fires when there is no
// existing timezone at all; a timezone the user already set (manually or via
// calendar auto-detection) is never touched.
export const getBrowserTimezoneInfo = () => {
  const zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { utcOffset: getTimezoneOffset(zoneName), zoneName };
};

export const buildDefaultAvailabilityData = () => {
  const zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    weekdays: DEFAULT_AVAILABILITY_DAYS.map(day => ({
      day,
      from: DEFAULT_AVAILABILITY_FROM,
      to: DEFAULT_AVAILABILITY_TO,
    })),
    time: { timeZone: { utcOffset: getTimezoneOffset(zoneName), zoneName } },
    defaultInPersonLocation: '',
    defaultOnlineLink: '',
    // Both coaches and participants default to Viveka's built-in video call
    // until they explicitly pick something else, so sessions are schedulable
    // right away without anyone having to open settings first.
    defaultVideoCall: { platform: 'viveka', meetingLink: '' },
  };
};

// Tracks the in-flight default-availability save fired from the signup
// thunks (Auth.duck.js). The post-signup redirect to the dashboard is
// driven by isAuthenticated flipping true, which happens before that save
// is even sent — so the dashboard's own "is availability empty" check can
// easily run and read an empty doc before the default has landed. Rather
// than guess how long that takes, the signup thunk registers its save
// promise here and the dashboard awaits it before checking, so there's no
// timing window at all when a save really is pending, and zero added delay
// on every other (non-signup) dashboard load since there's nothing to wait for.
let pendingDefaultAvailabilitySave = null;

export const setPendingDefaultAvailabilitySave = promise => {
  pendingDefaultAvailabilitySave = promise;
  promise.catch(() => {}).then(() => {
    if (pendingDefaultAvailabilitySave === promise) {
      pendingDefaultAvailabilitySave = null;
    }
  });
};

export const waitForPendingDefaultAvailabilitySave = () =>
  pendingDefaultAvailabilitySave ? pendingDefaultAvailabilitySave.catch(() => {}) : Promise.resolve();

export const sendGoogleOAuthCode = code => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}settings/googleCalendar`,
    {
      code,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const sendOutlookOAuthCode = code => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}settings/outlookCalendarRedirect`,
    {
      code,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const sendTestCoachEmail = email => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}settings/send-test-coach-email`,
    {
      email,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const saveAppleCalendarCredentials = (appleId, appPassword) => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}settings/appleCalendar`,
    {
      appleId,
      appPassword,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const saveCalendarAvailabilitySetting = data => async (dispatch, getState, sdk) => {
  return axios.post(`${config.coachSaaSService}settings/calendarAvailability`, {
    data,
  },{
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

export const getCalendarAvailabilitySetting = () => async (dispatch, getState, sdk) => {
  return axios.get(`${config.coachSaaSService}settings/calendarAvailability`, {
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

// Participant (customer) availability — same contract as the coach variants
// above, but the backend persists under the customer collection. The bearer
// token identifies the caller, so a participant only ever writes their own doc.
export const saveParticipantCalendarAvailabilitySetting = data => async (dispatch, getState, sdk) => {
  return axios.post(`${config.coachSaaSService}settings/customerCalendarAvailability`, {
    data,
  },{
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

export const getParticipantCalendarAvailabilitySetting = () => async (dispatch, getState, sdk) => {
  return axios.get(`${config.coachSaaSService}settings/customerCalendarAvailability`, {
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

export const getAuthorAvailabilityByActivityId = (TxId, providerId) => async (dispatch, getState, sdk) => {
  return axios.post(`${config.coachSaaSService}settings/getAuthorAvailabilityByActivityId`, {
    activityid: TxId,
    providerid: providerId,
  } ,{
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

// Public-safe status check for the OTHER party's calendar (e.g. a participant
// checking a coach's calendar before booking). Unauthenticated on purpose —
// privateData (where the connection tokens live) is never readable cross-user,
// so this hits a dedicated public endpoint that returns only {status, lastSyncedAt}.
export const getCalendarSyncStatusByProviderId = providerId => async (dispatch, getState, sdk) => {
  return axios.get(`${config.coachSaaSService}settings/calendarSyncStatus`, {
    params: { providerId },
  });
};

export const disconnectCalendar = (calendar) => async (dispatch, getState, sdk) => {
  return axios.post(`${config.coachSaaSService}settings/disconnectCalendar`, {
    calendar: calendar,
  } ,{
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  });
};

// export const updateCoachBranding = (values) => async (dispatch, getState, sdk) => {
//   return axios.post(`${config.coachSaaSService}settings/update-coach-branding`, {
//     values,
//   } ,{
//     headers: {
//       'content-type': 'application/json',
//       Authorization: await getBearerToken(sdk),
//     },
//   });
// };
